import apcv from '@assets/img/apcv.svg'
import cartevital from '@assets/img/cartevital.png'
import logoADRi from '@assets/img/logoADRi.png'
import logoALDi from '@assets/img/logoALDi.png'
import logoIMTi from '@assets/img/logoIMTi.png'
import logoInsi from '@assets/img/logoINSi.png'
import { SpeedDial, SpeedDialAction, Tooltip } from '@mui/material'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import { doGetOrder } from '@state/reducers/orderReducer'
import {
  Ambulance,
  ContactIcon,
  FileTextIcon,
  InfoIcon,
  InspectionPanel,
  QrCodeIcon,
  ShieldAlert,
  ShieldIcon,
  SmartphoneNfc,
} from 'lucide-react'
import moment from 'moment'
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { CouvertureDto, PatientReadSourceEnum } from '../../common/interfaces'
import { TransportForm } from '../../containers/app/ambulance/TransportForm'
import CouvertureForm, {
  CouvertureFormRef,
} from '../../containers/app/billing/form/CouvertureForm'
import { useAppDispatch, useAppSelector } from '../../hooks/reduxHooks'
import {
  AldiService,
  SearchAldiWithCarteVitaleDTO,
} from '../../services/AldiService'
import { Member, PatientDto, Site } from '../../services/api'
import { CreatePatientDTO, DocumentDTO } from '../../services/dtos'
import { PatientServiceV2 } from '../../services/PatientService'
import { doCreateALD, doFetchALD } from '../../state/reducers/aldReducer'
import {
  doGetPatientDocuments,
  doGetPatientInsHistory,
  setPatientDocuments,
  setPatientInsHistory,
  setSelectedInsiSearchResult,
} from '../../state/reducers/patientsReducer'
import { fetchOnePatientById } from '../../state/thunks/patientsThunk'
import { formatSsnV2 } from '../../utils/ssnUtil'
import DocumentListViewer from '../dialogs/DocumentListViewer'
import DocumentsList from '../documents/DocumentsList'
import PatientAldList from '../PatientAldDetails'
import PatientContactInfos from '../patientContactInfos'
import AldiSearchResult from './AldiSearchResult'
import InsiVerificationComponent from './InsiVerificationComponent'
import PatientFormV2, { PatientFormRef } from './PatientFormV2'
import PatientInsiInfos, { PatientInsiInfosRef } from './PatientInsiInfos'
import { EntityNameEnum } from '@utils/enums'

export type ActiveFormType =
  | 'PatientInfos'
  | 'ContactInfos'
  | 'Couverture'
  | 'Transport'
  | 'Documents'
  | 'INS'
  | 'ALD'

export type ManagepatientComponentRef = {
  getCouvertureFormRef: () => CouvertureFormRef | null
  getPatientFormRef: () => PatientFormRef | null
  setIsCoverageSet: (isSet: boolean) => void
  getDocuments: () => DocumentDTO[]
  setActiveForm: (form: ActiveFormType) => void
  clear: () => void
}

type Props = {
  patientId?: number
  orderId?: number
  visitId?: number
  onPatientFoundByVCard?: (patient: PatientDto) => void
  onFetchingCoverageUpdated?: (updated: boolean) => void
}

const DOCUMENTS_PAGE_SIZE = 10

const ManagePatientComponent = forwardRef<ManagepatientComponentRef, Props>(
  (
    { patientId, orderId, onPatientFoundByVCard, onFetchingCoverageUpdated },
    ref,
  ) => {
    useImperativeHandle(ref, () => ({
      getCouvertureFormRef: () => couvertureFormRef.current,
      getPatientFormRef: () => patientFormRef.current,
      setIsCoverageSet: (isSet: boolean) => {
        setIsCoverageSet(isSet)
      },
      getDocuments: () => paginatedDocuments.datas,
      setActiveForm: (form: ActiveFormType) => {
        setActiveForm(form)
      },
      clear: () => {
        patientFormRef.current?.reset()
        couvertureFormRef.current?.reset()
        dispatch(setPatientDocuments({ datas: [], totalCount: 0 }))
      },
    }))
    const dispatch = useAppDispatch()

    // ===================================== STATE =====================================

    const [activeForm, setActiveForm] = useState<ActiveFormType>('PatientInfos')
    const [documentsPage, setDocumentsPage] = useState(1)
    const patientFormRef = useRef<PatientFormRef>(null)
    const couvertureFormRef = useRef<CouvertureFormRef>(null)
    const patientInsiInfosRef = useRef<PatientInsiInfosRef>(null)
    const autoAldiFetchInProgressRef = useRef(false)
    const [isCoverageSet, setIsCoverageSet] = useState(false)
    const [isForeignPatient, setIsForeignPatient] = useState(false)

    const {
      paginatedDocuments,
      memberSituation,
      sites,
      order,
      currentPatient,
    } = useAppSelector((state) => ({
      sites: (state.auth.user as Member).sites as Site[],
      paginatedDocuments: state.patients.documents,
      memberSituation: state.order.selectedMemberSituation,
      order: state.order.selectedOrder,
      currentPatient: state.patients.currentPatientInfo,
    }))
    const [isDocumentViewerOpen, setIsDocumentViewerOpen] = useState(false)
    const [selectedDocument, setSelectedDocument] =
      useState<DocumentDTO | null>(null)
    const [isAldiDialogOpen, setIsAldiDialogOpen] = useState(false)
    const [isInsiSearchDialogOpen, setIsInsiSearchDialogOpen] = useState(false)

    // ===================================== EFFECTS =====================================

    useEffect(() => {
      if (orderId) {
        dispatch(doGetOrder(orderId)).unwrap()
      }

      if (patientId) {
        dispatch(
          fetchOnePatientById({
            id: patientId,
          }),
        )
          .unwrap()
          .then((patient) => {
            patientFormRef.current?.setValues({
              ...patient,
              birthDate: patient.birthDate ? moment(patient.birthDate) : null,
            } as any as CreatePatientDTO)
          })
        dispatch(
          doGetPatientDocuments({
            patientId,
            page: 1,
            take: DOCUMENTS_PAGE_SIZE,
          }),
        )
        dispatch(doGetPatientInsHistory(patientId))
      } else {
        dispatch(setPatientInsHistory(null))
      }
      return () => {
        dispatch(setSelectedInsiSearchResult(null))
      }
    }, [patientId])

    useEffect(() => {
      const copyButton = document.getElementById('copy-patient-info-btn')
      if (copyButton) {
        const handleCopyPatientInfo = async () => {
          const patientData = await patientFormRef.current?.getValues({
            ignoreValidation: true,
          })
          if (patientData && couvertureFormRef.current) {
            const birthDate = patientData.birthDate
              ? moment(patientData.birthDate).format('YYYYMMDD')
              : patientData.birthDateStr
              ? moment(patientData.birthDateStr, 'DD-MM-YYYY').format(
                  'YYYYMMDD',
                )
              : ''
            couvertureFormRef.current.setValues({
              beneficiairePrenom: patientData.firstName || '',
              beneficiaireNom: patientData.lastName || '',
              beneficaireDateNaissance: birthDate || '',
              // assureNomUsuel: patientData.lastName || '',
              // assurePrenom: patientData.firstName || '',
              // assureNom: patientData.lastName || '',
            } as any)
          }
        }
        copyButton.onclick = handleCopyPatientInfo
        return () => {
          copyButton.onclick = null
        }
      }
    }, [activeForm])

    // ===================================== FUNCTIONS =====================================

    const handleForeignerChange = (isForeigner: boolean) => {
      setIsForeignPatient(isForeigner)
    }

    const getCoverageIcon = () => {
      if (isCoverageSet) {
        return <ShieldIcon style={{ marginRight: 4 }} size={16} />
      }
      return <ShieldAlert style={{ marginRight: 4 }} size={16} />
    }

    const fetchDocuments = (page: number = documentsPage) => {
      if (patientId) {
        dispatch(
          doGetPatientDocuments({
            patientId,
            page,
            take: DOCUMENTS_PAGE_SIZE,
          }),
        )
      }
    }

    const handleDocumentsPageChange = (page: number) => {
      setDocumentsPage(page)
      fetchDocuments(page)
    }

    const handleDocumentDeleted = (_document: DocumentDTO) => {
      fetchDocuments()
    }

    const handleOnFileUploaded = (_document: DocumentDTO) => {
      setDocumentsPage(1)
      fetchDocuments(1)
    }

    const checkAutoAldiFetchEnabled = () => {
      return sites.some((s) => s.isAldiAutoReadEnabled)
    }

    const getAldiBirthDate = (
      coverageValues: CouvertureDto | null | undefined,
      patientValues: CreatePatientDTO | null | undefined,
    ) => {
      const coverageBirth = coverageValues?.beneficaireDateNaissance
        ? moment(coverageValues.beneficaireDateNaissance, [
            'YYYY-MM-DD',
            'YYYYMMDD',
          ])
        : null
      if (coverageBirth && coverageBirth.isValid()) {
        return coverageBirth.format('YYYY-MM-DD')
      }

      const patientBirth = patientValues?.birthDate
        ? moment(patientValues.birthDate)
        : null
      if (patientBirth && patientBirth.isValid()) {
        return patientBirth.format('YYYY-MM-DD')
      }

      const patientBirthStr = patientValues?.birthDateStr
        ? moment(patientValues.birthDateStr, 'DD-MM-YYYY')
        : null
      if (patientBirthStr && patientBirthStr.isValid()) {
        return patientBirthStr.format('YYYY-MM-DD')
      }

      return ''
    }

    const triggerAutoAldiSearchAndSync = async (
      fallbackCoverage?: CouvertureDto,
    ) => {
      if (autoAldiFetchInProgressRef.current) {
        return
      }
      if (!patientId) {
        return
      }

      autoAldiFetchInProgressRef.current = true

      try {
        if (!memberSituation) {
          return
        }

        const patientValues = await patientFormRef.current?.getValues()
        if (!patientValues) {
          return
        }

        const coverageValues =
          (await couvertureFormRef.current?.getValues(false)) ||
          fallbackCoverage ||
          null
        if (!coverageValues) {
          return
        }

        const birthDate = getAldiBirthDate(coverageValues, patientValues)
        if (!birthDate) {
          return
        }

        const sanitize = (value?: string | number | null) =>
          value !== undefined && value !== null
            ? String(value).replace(/\s+/g, '')
            : ''

        const psModel = {
          psNumeroFacturation:
            memberSituation.situation.numIdentificationPSFacturation,
          psIdentStructure:
            memberSituation.situation.numIdentificationStructure,
          authentificationMode: parseInt(
            process.env.REACT_APP_AUTH_MODE || '1',
            10,
          ),
          codePorteurCPS: process.env.REACT_APP_CODE_PORTEUR_CPS || '0',
          RPPS: memberSituation.member.rppsNumber || '',
        }

        const aldiPayload: SearchAldiWithCarteVitaleDTO = {
          psModel,
          aldiCV: {
            assureNumSecu:
              sanitize(coverageValues.assureNir) || sanitize(patientValues.ssn),
            beneficiaireDateNaissance: birthDate,
            beneficiaireRang: sanitize(coverageValues.rangNaissance) || '1',
            beneficiaireNumSecu:
              sanitize(coverageValues.beneficiaireNir) ||
              sanitize(patientValues.ssn),
            codeRegime: coverageValues.organismeAmo?.regime || '',
            codeCaisse: coverageValues.organismeAmo?.caisse || '',
            codeCentre: coverageValues.organismeAmo?.centre || '',
            numeroSerieCv: sanitize(coverageValues.numeroSerieCv),
          },
          fichierInjecte: {
            fichierBase64: '',
          },
          assertions: {
            assertionPs: '',
            assertionVitale: '',
            cpsBase64AuthCertificate: '',
            cpsBase64SignatureCertificate: '',
          },
          isApcv: coverageValues.readSource === PatientReadSourceEnum.Apcv,
          idJfse: memberSituation.situation.idJfse,
        }

        const response = await AldiService.searchWithCarteVitale(aldiPayload)
        const aldiList = response.message?.listeAld || []
        if (!aldiList.length) {
          return
        }

        const existingAlds = await dispatch(doFetchALD(patientId)).unwrap()
        const newAlds = aldiList.filter((aldiAld) => {
          return !existingAlds.some(
            (existingAld) => existingAld.code === aldiAld.code,
          )
        })

        if (!newAlds.length) {
          return
        }

        await Promise.all(
          newAlds.map((aldiAld) => {
            const aldData = {
              libelle: aldiAld.libelle,
              code: aldiAld.code,
              etat: aldiAld.etat?.toLowerCase() === 'oui' ? 'Actif' : 'Inactif',
              dateFin: aldiAld.dateFin || undefined,
              cimCode: aldiAld.codeCim || undefined,
              source: 'ALDi' as const,
              patientId,
            }

            return dispatch(doCreateALD(aldData as any)).unwrap()
          }),
        )
      } catch (error) {
        console.error('ALD auto-sync failed', error)
      } finally {
        autoAldiFetchInProgressRef.current = false
      }
    }

    const handleVitalCardReadFinished = async (dto: CouvertureDto) => {
      const identifier = PatientServiceV2.getIdentifier(
        dto.assureNir || '',
        dto.beneficiairePrenom || '',
        dto.beneficaireNomUsuel || dto.beneficiaireNom || '',
        dto.beneficaireDateNaissance || '',
      )

      let patient: Partial<PatientDto> | null = null

      if (identifier) {
        try {
          const existingPatient = await PatientServiceV2.getByIdentifier(
            identifier,
            dto.assureNir,
          )

          if (existingPatient) {
            patient = existingPatient
            onPatientFoundByVCard?.(existingPatient as PatientDto)
          }
        } catch (err) {
          console.error('Error checking existing patient:', err)
        }
      }
      if (!patient) {
        patient = {
          firstName: dto.beneficiairePrenom,
          lastName: dto.beneficiaireNom || dto.beneficaireNomUsuel || undefined,
          birthDate: dto.beneficaireDateNaissance as any,
          ssn: dto.beneficiaireNir,
          assureNir: dto.assureNir,
          identifier,
        }
      }
      const ssn = dto.assureNir
      if (ssn) {
        patient.ssn = formatSsnV2(ssn)
      }

      patientFormRef.current?.setValues({
        ...patient,
        identifier,
      } as any as CreatePatientDTO)

      if (checkAutoAldiFetchEnabled()) {
        triggerAutoAldiSearchAndSync(dto)
      }
    }

    const handleSearchAldi = async () => {
      setIsAldiDialogOpen(true)
    }

    const handleCloseAldiDialog = () => {
      setIsAldiDialogOpen(false)
    }

    return (
      <div className="pb-8">
        {currentPatient && (
          <div className="pb-1">
            <p className="text-base font-semibold text-gray-900">
              {currentPatient.firstName} {currentPatient.lastName}
            </p>
            <p className="text-sm text-gray-500">
              {currentPatient.birthDate &&
                moment(currentPatient.birthDate).format('DD/MM/YYYY')}
              {currentPatient.birthDate && currentPatient.ssn && ' — '}
              {currentPatient.ssn && formatSsnV2(currentPatient.ssn)}
            </p>
          </div>
        )}
        <div className="flex space-between ">
          <ToggleButtonGroup
            value={activeForm}
            exclusive
            onChange={(_, v) => {
              if (v) {
                setActiveForm(v)
              }
            }}
            size="small"
            sx={{
              position: 'sticky',
              top: '0',
              zIndex: 1001,
              backgroundColor: 'white',
              marginTop: 2,
            }}
          >
            <ToggleButton sx={{ width: '100px' }} value="PatientInfos">
              <InfoIcon size={16} style={{ marginRight: 4 }} />
              Infos
            </ToggleButton>
            <ToggleButton
              disabled={isForeignPatient}
              sx={{ width: '140px' }}
              value="Couverture"
              color={!isCoverageSet ? 'error' : 'primary'}
            >
              {getCoverageIcon()}
              Couverture
            </ToggleButton>
            <ToggleButton
              sx={{ width: '160px' }}
              value="ContactInfos"
              disabled={!patientId}
            >
              <ContactIcon size={16} style={{ marginRight: 4 }} />
              Contact Infos
            </ToggleButton>
            <ToggleButton
              sx={{ width: '140px' }}
              value="Transport"
              disabled={!orderId}
            >
              <Ambulance size={16} style={{ marginRight: 4 }} />
              Transport
            </ToggleButton>
            <ToggleButton sx={{ width: '140px' }} value="Documents">
              <FileTextIcon size={16} style={{ marginRight: 4 }} />
              Documents
            </ToggleButton>
            <ToggleButton sx={{ width: '80px' }} value="INS">
              INS
            </ToggleButton>
            <ToggleButton sx={{ width: '80px' }} value="ALD">
              ALD
            </ToggleButton>
          </ToggleButtonGroup>
          {/* actions   */}
          {!isForeignPatient && (
            <div className="fixed right-4 z-50 top-[100px]">
              <div className="flex flex-col gap-3 bg-white/80 backdrop-blur-sm p-3 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
                <Tooltip title="Lire la carte vitale" placement="left">
                  <button
                    type="button"
                    onClick={() =>
                      couvertureFormRef.current?.openVitalCardReader()
                    }
                    className="relative group rounded-xl p-2 bg-gradient-to-br from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 transform hover:-translate-x-1 hover:scale-105 transition-all duration-300 flex items-center border border-blue-200 overflow-hidden"
                  >
                    <div className="relative flex-shrink-0">
                      <img
                        src={cartevital}
                        alt="carte vitale"
                        className="w-[50px] transition-all duration-300 group-hover:scale-110 group-hover:rotate-6"
                      />
                      <div className="absolute inset-0 bg-blue-400 opacity-0 group-hover:opacity-10 transition-opacity duration-300 rounded-lg" />
                    </div>
                  </button>
                </Tooltip>
                <Tooltip title="Recherche INSi" placement="left">
                  <button
                    type="button"
                    onClick={() => {
                      setIsInsiSearchDialogOpen(true)
                      setActiveForm('INS')
                    }}
                    className="relative group rounded-xl p-2 bg-gradient-to-br from-green-50 to-green-100 hover:from-green-100 hover:to-green-200 transform hover:-translate-x-1 hover:scale-105 transition-all duration-300 flex items-center border border-green-200 overflow-hidden"
                  >
                    <div className="relative flex-shrink-0">
                      <img
                        src={logoInsi}
                        alt="logoInsi"
                        className="w-[50px] transition-all duration-300 group-hover:scale-110 group-hover:rotate-6"
                      />
                      <div className="absolute inset-0 bg-green-400 opacity-0 group-hover:opacity-10 transition-opacity duration-300 rounded-lg" />
                    </div>
                  </button>
                </Tooltip>
                <Tooltip title="Appeler ADRi" placement="left">
                  <button
                    type="button"
                    className="relative group rounded-xl p-2 bg-gradient-to-br from-red-50 to-red-100 hover:from-red-100 hover:to-red-200 transform hover:-translate-x-1 hover:scale-105 transition-all duration-300 flex items-center border border-red-200 overflow-hidden"
                  >
                    <img
                      src={logoADRi}
                      alt="logoADRi"
                      className="w-[50px] transition-all duration-300 group-hover:scale-110 group-hover:rotate-6"
                    />
                  </button>
                </Tooltip>

                <Tooltip title="Appeler ALDi" placement="left">
                  <button
                    type="button"
                    onClick={handleSearchAldi}
                    className="relative group rounded-xl p-2 bg-gradient-to-br from-red-50 to-red-100 hover:from-red-100 hover:to-red-200 transform hover:-translate-x-1 hover:scale-105 transition-all duration-300 flex items-center border border-red-200 overflow-hidden"
                  >
                    <img
                      src={logoALDi}
                      alt="logoALDi"
                      className="w-[50px] transition-all duration-300 group-hover:scale-110 group-hover:rotate-6"
                    />
                  </button>
                </Tooltip>
                <Tooltip title="Appeler IMTI" placement="left">
                  <button
                    type="button"
                    onClick={() => couvertureFormRef.current?.openImtiSearch()}
                    className="relative group rounded-xl p-2 bg-gradient-to-br from-red-50 to-red-100 hover:from-red-100 hover:to-red-200 transform hover:-translate-x-1 hover:scale-105 transition-all duration-300 flex items-center border border-red-200 overflow-hidden"
                  >
                    <img
                      src={logoIMTi}
                      alt="logoIMTI"
                      className="w-[50px] transition-all duration-300 group-hover:scale-110 group-hover:rotate-6"
                    />
                  </button>
                </Tooltip>
                <div
                  className="relative group rounded-xl bg-gradient-to-br from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 transform hover:-translate-x-1 hover:scale-105 transition-all duration-300 border border-blue-200 overflow-visible"
                  style={{ width: '64px', height: '64px' }}
                >
                  <SpeedDial
                    ariaLabel="APCV actions"
                    icon={
                      <img
                        src={apcv}
                        alt="apcv"
                        className="w-[10mm] transition-all duration-300 group-hover:scale-110 group-hover:rotate-6"
                      />
                    }
                    direction="left"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      '& .MuiSpeedDial-fab': {
                        width: '100%',
                        height: '100%',
                        borderRadius: '0.75rem',
                        boxShadow: 'none',
                        background: 'transparent',
                      },
                      '& .MuiSpeedDial-actions': {
                        position: 'absolute',
                        left: '-180px',
                        top: '6px',
                        zIndex: 1500,
                        display: 'flex',
                        gap: '8px',
                        width: '220px',
                        justifyContent: 'flex-end',
                      },
                    }}
                  >
                    <SpeedDialAction
                      icon={<InspectionPanel size={24} />}
                      tooltipTitle="Restituer le contexte"
                      tooltipPlacement="top"
                      onClick={() => {
                        couvertureFormRef.current?.openRestoreContext()
                      }}
                      FabProps={{
                        sx: {
                          bgcolor: 'white',
                          '&:hover': {
                            bgcolor: '#f0f9ff',
                          },
                        },
                      }}
                    />
                    <SpeedDialAction
                      icon={<QrCodeIcon size={24} />}
                      tooltipTitle="QR Code"
                      tooltipPlacement="top"
                      onClick={() =>
                        couvertureFormRef.current?.openQRCodeReader()
                      }
                      FabProps={{
                        sx: {
                          bgcolor: 'white',
                          '&:hover': {
                            bgcolor: '#f0f9ff',
                          },
                        },
                      }}
                    />
                    <SpeedDialAction
                      icon={<SmartphoneNfc size={24} />}
                      tooltipTitle="NFC"
                      tooltipPlacement="top"
                      onClick={() => {
                        couvertureFormRef.current?.openNFCReader()
                      }}
                      FabProps={{
                        sx: {
                          bgcolor: 'white',
                          '&:hover': {
                            bgcolor: '#f0f9ff',
                          },
                        },
                      }}
                    />
                  </SpeedDial>
                </div>
              </div>
            </div>
          )}
        </div>

        <div
          style={{ display: activeForm === 'PatientInfos' ? 'block' : 'none' }}
        >
          <PatientFormV2
            ref={patientFormRef}
            onForeignerChange={handleForeignerChange}
          />
        </div>
        <div
          style={{ display: activeForm === 'ContactInfos' ? 'block' : 'none' }}
        >
          <PatientContactInfos patientId={patientId} />
        </div>

        <div
          style={{ display: activeForm === 'Couverture' ? 'block' : 'none' }}
        >
          <CouvertureForm
            ref={couvertureFormRef}
            onVitalCardReadFinished={handleVitalCardReadFinished}
            isForeigner={isForeignPatient}
            onFetchingUpdated={onFetchingCoverageUpdated}
          />
        </div>
        {activeForm === 'Transport' && order && (
          <div className="py-3">
            <TransportForm row={order} isOrder={Boolean(orderId)} />
          </div>
        )}
        {activeForm === 'Documents' && (
          <>
            <DocumentsList
              entityId={patientId}
              entityName={EntityNameEnum.PATIENT}
              documents={paginatedDocuments.datas}
              isEditingDocuments={isDocumentViewerOpen}
              setisEditingDocuments={setIsDocumentViewerOpen}
              onDocumentDeleted={handleDocumentDeleted}
              handleOnFileUploaded={handleOnFileUploaded}
              setSelectedDocument={setSelectedDocument}
              listClassName="max-h-[calc(100vh-360px)] overflow-y-auto"
              page={documentsPage}
              totalCount={paginatedDocuments.totalCount}
              pageSize={DOCUMENTS_PAGE_SIZE}
              onPageChange={handleDocumentsPageChange}
            />
            <DocumentListViewer
              documents={paginatedDocuments.datas}
              selectedDocument={selectedDocument}
              isOpen={selectedDocument !== null}
              onClose={() => {
                setSelectedDocument(null)
              }}
              onSelectedDocumentChange={setSelectedDocument}
            />
          </>
        )}
        <div style={{ display: activeForm === 'INS' ? 'block' : 'none' }}>
          <PatientInsiInfos
            ref={patientInsiInfosRef}
            isOpen={activeForm === 'INS'}
            patientId={patientId}
            setActiveForm={setActiveForm}
            getPatientValues={async (ignoreValidation: boolean) => {
              const getter = patientFormRef.current?.getValues
              if (!getter) {
                return null
              }
              return (await getter({ ignoreValidation })) || null
            }}
            getCoverageValues={async (shouldValidate?: boolean) => {
              const getter = couvertureFormRef.current?.getValues
              if (!getter) {
                return null
              }
              return (await getter(shouldValidate)) || null
            }}
          />
        </div>
        {activeForm === 'ALD' && patientId && (
          <PatientAldList patientId={patientId} />
        )}

        <InsiVerificationComponent
          open={isInsiSearchDialogOpen}
          onClose={() => setIsInsiSearchDialogOpen(false)}
          memberSituation={memberSituation}
          setActiveForm={setActiveForm}
          getPatientValues={async () => {
            const getter = patientFormRef.current?.getValues
            if (!getter) {
              return null
            }
            return (await getter({ ignoreValidation: true })) || null
          }}
          getCoverageValues={async () => {
            const getter = couvertureFormRef.current?.getValues
            if (!getter) {
              return null
            }
            return (await getter(false)) || null
          }}
          onSearchSuccess={() => {
            setActiveForm('INS')
            setIsInsiSearchDialogOpen(false)
          }}
          getInsiFormValues={async () => {
            const getter = patientInsiInfosRef.current?.getValues
            if (!getter) {
              return null
            }
            return (await getter()) || null
          }}
        />
        <AldiSearchResult
          open={isAldiDialogOpen}
          onClose={handleCloseAldiDialog}
          patientFormRef={patientFormRef}
          coverageFormRef={couvertureFormRef}
          patientId={patientId}
        />
      </div>
    )
  },
)

export default ManagePatientComponent
